import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import AnnouncementToast from "src/components/AnnouncementToast";
import { getAnnouncements } from "src/components/announcements/announcementRegistry";
import {
  getActiveAnnouncements,
  markAnnouncementSeen,
  snoozeAnnouncement,
  type Announcement,
} from "src/lib/announcements";
import { UserRole } from "src/lib/roles";
import { selectUserProfile } from "src/store/authSlice";

const nunito = { fontFamily: "'Nunito', system-ui, sans-serif" } as const;

/** How many cards stack (desktop) before the rest collapse into "+N more". */
const MAX_VISIBLE_DESKTOP = 2;

/**
 * Post-login announcement stack (bottom-right). Shows every eligible entry from
 * the registry, highest priority on top, capped at {@link MAX_VISIBLE}; the
 * primary CTA and X dismiss permanently, "Not now" snoozes. Dismissing a card
 * promotes the next queued one into view.
 */
const AnnouncementCenter: React.FC = () => {
  const navigate = useNavigate();
  const userProfile = useSelector(selectUserProfile);
  const role = userProfile?.role ?? UserRole.VIEWER;
  // Snapshot eligibility once the role is known (post-login) so time-based and
  // role-based gates plus persisted state are read together.
  const eligible = useMemo(
    () =>
      getActiveAnnouncements(getAnnouncements(), { nowMs: Date.now(), role }),
    [role],
  );
  // Ids acted on this session — removed from the stack without a remount.
  const [dismissed, setDismissed] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [ready, setReady] = useState(false);
  // One card on phones (the stack otherwise covers half the screen); more on
  // wider viewports. Extras collapse into the "+N more" note either way.
  const [maxVisible, setMaxVisible] = useState(1);

  useEffect(() => {
    if (eligible.length === 0) return;
    const timer = window.setTimeout(() => setReady(true), 900);
    return () => window.clearTimeout(timer);
  }, [eligible.length]);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 640px)");
    const apply = () => setMaxVisible(mq.matches ? MAX_VISIBLE_DESKTOP : 1);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  const remaining = eligible.filter((a) => !dismissed.has(a.id));
  const visible = remaining.slice(0, maxVisible);
  const overflow = remaining.length - visible.length;

  if (!ready || remaining.length === 0) return null;

  const remove = (id: string) => setDismissed((prev) => new Set(prev).add(id));

  const dismiss = (a: Announcement) => {
    markAnnouncementSeen(a.id);
    remove(a.id);
  };

  const snooze = (a: Announcement) => {
    if (a.snoozeMs != null) snoozeAnnouncement(a.id, a.snoozeMs);
    remove(a.id);
  };

  const runPrimary = (a: Announcement) => {
    markAnnouncementSeen(a.id);
    remove(a.id);
    const { to, href, onClick } = a.primary;
    if (onClick) onClick();
    else if (to) navigate(to);
    else if (href) window.open(href, "_blank", "noopener,noreferrer");
  };

  return (
    <div
      className="fixed bottom-[max(1rem,env(safe-area-inset-bottom))] right-[max(1rem,env(safe-area-inset-right))] z-[60] flex w-[min(100vw-2rem,22rem)] flex-col gap-2"
      style={nunito}
      role="region"
      aria-label="Announcements"
      aria-live="polite"
    >
      <AnimatePresence initial>
        {visible.map((a) => (
          <motion.div
            key={a.id}
            layout
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ type: "spring", stiffness: 420, damping: 32 }}
          >
            <AnnouncementToast
              announcement={a}
              onPrimary={() => runPrimary(a)}
              onSnooze={() => snooze(a)}
              onDismiss={() => dismiss(a)}
            />
          </motion.div>
        ))}
      </AnimatePresence>

      {overflow > 0 && (
        <motion.p
          layout
          className="text-center text-xs font-bold text-gray-400"
        >
          +{overflow} more update{overflow === 1 ? "" : "s"}
        </motion.p>
      )}
    </div>
  );
};

export default AnnouncementCenter;
